import React from 'react';
import { WebsiteDesignProps } from './types';
import { Hexagon, Database, Activity, Code } from 'lucide-react';
import { Button } from "@/components/ui/button";

const HighTechLab: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "BioSyn Academy";

    return (
        <div className="bg-slate-50 min-h-screen font-mono text-slate-800">
            {/* Hex Background */}
            <div className="fixed inset-0 pointer-events-none opacity-5" style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='24' height='40' viewBox='0 0 24 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 40c5.523 0 10-4.477 10-10V10c0-5.523 4.477-10 10-10s10 4.477 10 10v20c0 5.523-4.477 10-10 10S0 35.523 0 30V40z' fill='%2394a3b8' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`}}></div>

            <nav className="h-16 bg-white border-b border-cyan-500/30 flex items-center justify-between px-8 relative z-20">
                <div className="flex items-center gap-2 text-cyan-600 font-bold tracking-tighter">
                    <Hexagon size={24} className="fill-cyan-100" /> {schoolName}
                </div>
                <div className="flex items-center gap-4 text-xs font-bold text-slate-500">
                    <div className="flex items-center gap-1"><div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div> SYSTEM_READY</div>
                    <Button variant="outline" className="border-cyan-500 text-cyan-600 hover:bg-cyan-50 h-8 text-xs font-bold">ACCESS_PORTAL</Button>
                </div>
            </nav>

            <main className="container mx-auto px-8 py-16 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                         <div className="inline-block bg-cyan-100 text-cyan-700 px-2 py-1 text-xs font-bold mb-6 border border-cyan-200">
                             :: INITIALIZING SEQUENCE ::
                         </div>
                         <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6 leading-none tracking-tight">
                             Engineering <br/> <span className="text-cyan-600">Tomorrow.</span>
                         </h1>
                         <p className="text-lg text-slate-600 mb-8 max-w-lg border-l-2 border-cyan-500 pl-4 py-2">
                             Advanced curriculum for the architects of the future. Robotics, AI, and Bio-Tech integration.
                         </p>
                         <div className="flex gap-4">
                             <Button className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold h-12 px-8 shadow-[0_4px_14px_0_rgba(8,145,178,0.39)]">
                                 Start Simulation
                             </Button>
                             <Button variant="ghost" className="text-cyan-700 hover:bg-cyan-50 h-12 px-8 font-bold border border-cyan-200">
                                 Read Data Logs
                             </Button>
                         </div>
                    </div>
                    
                    <div className="relative">
                         <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg blur opacity-20"></div>
                         <div className="relative bg-white border border-slate-200 p-2 rounded-lg shadow-xl">
                             <div className="aspect-video bg-slate-900 rounded overflow-hidden relative group">
                                 <img src="https://images.unsplash.com/photo-1581092921461-eab62e97a780?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" alt="Lab" />
                                 {/* HUD Overlay */}
                                 <div className="absolute inset-0 pointer-events-none p-4 flex flex-col justify-between">
                                      <div className="flex justify-between text-cyan-400 text-[10px]">
                                          <span>REC ●</span>
                                          <span>[TARGET_LOCKED]</span>
                                      </div>
                                      <div className="border border-cyan-500/50 w-16 h-16 rounded-full self-center flex items-center justify-center">
                                          <div className="w-1 h-1 bg-cyan-400"></div>
                                      </div>
                                      <div className="text-cyan-400 text-[10px] font-mono whitespace-pre">
                                          COORD: 34.552, 12.331
                                      </div>
                                 </div>
                             </div>
                         </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-24">
                     {[
                         { icon: Code, title: "Algorithm Design", val: "98%" },
                         { icon: Activity, title: "Bio-Metrics", val: "ACTIVE" },
                         { icon: Database, title: "Data Science", val: "LOADED" }
                     ].map((item, i) => (
                         <div key={i} className="bg-white border border-slate-200 p-6 rounded hover:border-cyan-400 transition-colors cursor-pointer group shadow-sm">
                             <div className="flex justify-between items-start mb-4">
                                 <item.icon className="text-slate-400 group-hover:text-cyan-500 transition-colors" />
                                 <span className="text-xs font-bold text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded">{item.val}</span>
                             </div>
                             <h3 className="text-lg font-bold text-slate-800">{item.title}</h3>
                         </div>
                     ))}
                </div>
            </main>
        </div>
    );
};

export default HighTechLab;
